import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import type { CompanySortOptions } from '@/api/v4/organization.api';
import type { GenAiRequestHistoryRound } from '@/api/v4/genai-request-history.api';
import type { QueryKey } from '@/config/query-client';
import { useBlockNavigation } from '@/routes/hooks/use-block-navigation';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { colors } from '@/theme/colors';
import { theme } from '@/theme/setupTheme';
import type { IOption } from '@/types';
import { Icon } from '@/ui/icons/icon';
import { Inline, Stack } from '@/ui/line/line';
import { TableBody } from '@/ui/table/infinite-table/table-body';
import { TableHeaderCell } from '@/ui/table/infinite-table/table-header-cell';
import { useAtom, useSetAtom } from 'jotai';

import { buildExploreExternalPrefillNavigateState } from '../explore-navigation-entry';
import type { ExampleCompany, ExploreResult } from '../explore.state';
import { exploreHistoryPaneModeState } from '../explore.state';
import { ExploreHistoryPane } from '../history/explore-history-pane';
import {
  getPromptSinceRoundNumber,
  getRoundFilterDeltas,
  isFilterOnlyRound,
} from '../refine/explore-round-display';
import { isRegeneratePanelOpenState } from '../regenerate/regenerate-explore.state';
import { RegeneratePanel } from '../regenerate/regenerate-panel';
import { useRegenerateExploreForm } from '../regenerate/use-regenerate-explore-form';

import { CompanyDataPrefetcher } from './cells/use-company-prefetcher';
import { useExploreResultsTableConfig } from './use-explore-results-table-config';
import { useSortExploreResults } from './use-sort-explore-results';

export const ExploreResults = ({
  dataQueryKey,
  inputData,
  latestRoundNumber,
  onViewingRoundChange,
  requestId,
  roundCount,
  rounds,
  searchTitle,
  suggestionData,
  viewingRoundNumber,
}: {
  dataQueryKey: QueryKey;
  inputData: {
    description: string;
    exampleCompanies: ExampleCompany[];
    locations: IOption[];
  };
  latestRoundNumber: number;
  onViewingRoundChange: (roundNumber: number) => void;
  requestId: string;
  roundCount: number;
  rounds: GenAiRequestHistoryRound[];
  searchTitle?: string;
  suggestionData: ExploreResult[];
  viewingRoundNumber: number;
}) => {
  const { t } = useTranslation('explore');
  const navigateTo = useNavigateTo();
  const [isRegeneratePanelOpen, setRegeneratePanelOpen] = useAtom(
    isRegeneratePanelOpenState,
  );
  const setPaneMode = useSetAtom(exploreHistoryPaneModeState);
  const { rows, headers } = useExploreResultsTableConfig({
    suggestions: suggestionData,
    requestId,
    dataQueryKey,
  });
  const { handleSort, sort } = useSortExploreResults();
  const { resetForm, checkIsDirty } = useRegenerateExploreForm();

  const defaultValues = useMemo(() => {
    return {
      defaultDescription: inputData?.description,
      defaultExampleCompanies: inputData?.exampleCompanies,
      defaultLocations: inputData?.locations,
    };
  }, [inputData]);

  const isRegenerateFormDirty = checkIsDirty(defaultValues);

  useEffect(() => {
    setPaneMode('rounds');
    return () => {
      setPaneMode('history');
    };
  }, [setPaneMode]);

  const onChangeRoute = () => {
    resetForm();
    setRegeneratePanelOpen(false);
  };

  useBlockNavigation({
    when: isRegeneratePanelOpen && isRegenerateFormDirty,
    additionalActionOnConfirm: onChangeRoute,
  });

  const crunchbaseIds = useMemo(() => {
    return suggestionData
      .filter(
        suggestion => !suggestion.organizationId && suggestion.crunchbaseId,
      )
      .map(suggestion => suggestion.crunchbaseId as string);
  }, [suggestionData]);

  const sortedRounds = useMemo(
    () =>
      [...rounds].sort((left, right) => left.roundNumber - right.roundNumber),
    [rounds],
  );

  const activeRoundIndex = useMemo(
    () =>
      sortedRounds.findIndex(round => round.roundNumber === viewingRoundNumber),
    [sortedRounds, viewingRoundNumber],
  );

  const filterOnly =
    activeRoundIndex >= 0 &&
    isFilterOnlyRound({ roundIndex: activeRoundIndex, rounds: sortedRounds });

  const filterDeltas = useMemo(() => {
    if (!filterOnly || activeRoundIndex <= 0) {
      return [];
    }
    const previous = sortedRounds[activeRoundIndex - 1];
    const current = sortedRounds[activeRoundIndex];
    if (!previous || !current) {
      return [];
    }
    return getRoundFilterDeltas({ current, previous });
  }, [activeRoundIndex, filterOnly, sortedRounds]);

  const promptTag = filterOnly
    ? t('promptUnchangedSince', {
        round: getPromptSinceRoundNumber({
          roundIndex: activeRoundIndex,
          rounds: sortedRounds,
        }),
      })
    : t`promptForThisRound`;

  const handleReusePrompt = () => {
    navigateTo.explore(undefined, undefined, {
      state: buildExploreExternalPrefillNavigateState({
        description: inputData.description,
      }),
    });
  };

  return (
    <Relative>
      <Layout>
        <SidePane>
          <ExploreHistoryPane
            onViewingRoundChange={onViewingRoundChange}
            roundChain={{
              inputData,
              latestRoundNumber,
              requestId,
              roundCount,
              rounds,
              searchTitle,
              viewingRoundNumber,
            }}
          />
        </SidePane>
        <Main>
          <Container gap="16px">
            <ResultsHeader gap="4px">
              <Pill>
                <Icon
                  icon="Sprinkles"
                  width="10px"
                  height="10px"
                  color={colors.blue.primaryA}
                />
                <span>
                  {t('roundOf', {
                    current: viewingRoundNumber,
                    total: roundCount,
                  })}
                  {filterDeltas.length
                    ? ` · ${filterDeltas
                        .map(delta =>
                          t(delta.kind, { items: delta.items.join(', ') }),
                        )
                        .join(' · ')}`
                    : ''}
                </span>
              </Pill>
              {searchTitle ? <SearchTitle>{searchTitle}</SearchTitle> : null}
              <ResultCount>
                {t('companiesFound', { count: suggestionData.length })}
              </ResultCount>
            </ResultsHeader>

            <PromptCard>
              <PromptBody>
                <PromptTag>{promptTag}</PromptTag>
                <PromptText>{inputData.description}</PromptText>
              </PromptBody>
              <ReuseButton type="button" onClick={handleReusePrompt}>
                <Icon
                  icon="ArrowGoBack"
                  width="12px"
                  height="12px"
                  color={colors.blue.primaryA}
                />
                {t`reusePrompt`}
              </ReuseButton>
            </PromptCard>

            <CompanyDataPrefetcher crunchbaseIds={crunchbaseIds} />

            <Wrapper>
              <Table>
                <thead>
                  <tr>
                    {headers.cells.map(cell => (
                      <TableHeaderCell<CompanySortOptions | null>
                        key={cell.columnKey}
                        cell={cell}
                        sort={{
                          ...sort,
                          onSort: update =>
                            handleSort({ update, dataQueryKey, requestId }),
                        }}
                        sticky={false}
                      />
                    ))}
                  </tr>
                </thead>
                <TableBody rows={rows} borderColor={colors.gray.c3} />
              </Table>
            </Wrapper>
          </Container>
        </Main>
      </Layout>
      {isRegeneratePanelOpen && (
        <RegeneratePanel
          onClose={() => setRegeneratePanelOpen(false)}
          isLoading={!!requestId && !suggestionData}
          defaultValues={defaultValues}
          requestId={requestId}
          viewingRoundNumber={viewingRoundNumber}
          latestRoundNumber={latestRoundNumber}
        />
      )}
    </Relative>
  );
};

const Relative = styled('div')`
  position: relative;
  height: 100%;
`;

const Layout = styled('div')`
  display: grid;
  grid-template-columns: 248px minmax(0, 1fr);
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const SidePane = styled('div')`
  height: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.basics.white};
  border-right: 1px solid ${({ theme }) => theme.colors.gray.c3};
`;

const Main = styled('div')`
  min-width: 0;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: ${({ theme }) =>
    theme.palettes.component.explore.page.background};
`;

const Container = styled(Stack)`
  height: 100%;
  padding: 12px 24px 40px;
  overflow: hidden;
`;

const ResultsHeader = styled(Stack)``;

const Pill = styled(Inline)`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  width: fit-content;
  background: ${({ theme }) => theme.colors.blue.c8};
  color: ${({ theme }) => theme.colors.blue.primaryA};
  font-size: 11px;
  font-weight: 600;
  padding: 4px 9px;
  border-radius: 6px;
`;

const SearchTitle = styled('h2')`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.25;
  color: ${({ theme }) => theme.colors.basics.black};
`;

const ResultCount = styled('div')`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray.c11};
`;

const PromptCard = styled('div')`
  background: ${({ theme }) => theme.colors.gray.c1};
  border: 1px solid ${({ theme }) => theme.colors.gray.c3};
  border-radius: 10px;
  padding: 13px 15px;
  display: flex;
  gap: 14px;
  align-items: flex-start;
`;

const PromptBody = styled('div')`
  flex: 1;
  min-width: 0;
`;

const PromptTag = styled('div')`
  font-size: 9.5px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray.c10};
  margin-bottom: 5px;
`;

const PromptText = styled('p')`
  margin: 0;
  font-size: 12.5px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.gray.c12};
`;

const ReuseButton = styled('button')`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.blue.primaryA};
  background: ${({ theme }) => theme.colors.basics.white};
  border: 1px solid ${({ theme }) => theme.colors.gray.c3};
  border-radius: 7px;
  padding: 6px 10px;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.blue.c8};
    border-color: ${({ theme }) => theme.colors.blue.c4};
  }
`;

const Wrapper = styled('div')`
  overflow-y: auto;
  position: relative;
  flex: 1;
  min-height: 0;
  ${theme.mixins.scrollbar};
`;

const Table = styled('table')`
  background-color: ${({ theme }) => theme.colors.basics.white};
  width: calc(100% - 15px);

  td {
    background-color: transparent;
  }
`;
